import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';

interface Goal {
  id: number;
  title: string;
  description: string | null;
  goal_type: 'checkbox' | 'numeric';
  target_value: number | null;
  unit: string | null;
  frequency: 'daily' | 'weekly';
  todayValue?: number;
  weekValue?: number;
}

interface GoalCardProps {
  goal: Goal;
  onUpdate: () => void;
}

export default function GoalCard({ goal, onUpdate }: GoalCardProps) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleCheckboxLog = async () => {
    try {
      await api.logProgress(String(goal.id), { value: 1 });
      onUpdate();
    } catch (err) {
      alert('Failed to log progress');
    }
  };

  const handleNumericLog = async (delta: number) => {
    try {
      await api.logProgress(String(goal.id), { value: delta });
      onUpdate();
    } catch (err) {
      alert('Failed to log progress');
    }
  };

  const startEditing = () => {
    setEditing(true);
    setInputValue(String(goal.todayValue || 0));
  };

  const submitValue = async () => {
    const newValue = parseFloat(inputValue);
    if (isNaN(newValue) || newValue < 0) {
      setEditing(false);
      return;
    }
    try {
      await api.logProgress(String(goal.id), { value: newValue, absolute: true });
      setEditing(false);
      onUpdate();
    } catch (err) {
      alert('Failed to update value');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') submitValue();
    if (e.key === 'Escape') setEditing(false);
  };

  const isDoneToday = goal.todayValue && goal.todayValue >= (goal.target_value || 1);
  const percentComplete = goal.target_value && goal.target_value > 0
    ? Math.min(100, ((goal.todayValue || 0) / goal.target_value) * 100)
    : 0;

  return (
    <div className="bg-slate-800 rounded-2xl p-4 mb-3 border border-slate-700/50 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base truncate">{goal.title}</h3>
          {goal.description && (
            <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{goal.description}</p>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 uppercase tracking-wide">
              {goal.frequency}
            </span>
            {goal.unit && (
              <span className="text-[10px] text-slate-500">{goal.unit}</span>
            )}
          </div>
        </div>
        <button
          onClick={() => navigate(`goal/${goal.id}`)}
          className="text-slate-400 hover:text-white p-1.5 -mr-1"
        >
          ✏️
        </button>
      </div>

      {goal.goal_type === 'checkbox' ? (
        <button
          onClick={handleCheckboxLog}
          disabled={!!isDoneToday}
          className={`w-full py-3 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] transform ${
            isDoneToday
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'bg-primary-600 hover:bg-primary-500 text-white shadow-lg shadow-primary-600/20'
          }`}
        >
          {isDoneToday ? '✓ Done Today' : 'Mark as Done'}
        </button>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleNumericLog(-1)}
                className="w-12 h-12 flex items-center justify-center bg-slate-700 hover:bg-slate-600 rounded-xl text-xl font-bold transition-colors active:scale-90 transform"
              >
                −
              </button>
              <div className="w-24 text-center">
                {editing ? (
                  <input
                    ref={inputRef}
                    type="number"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onBlur={submitValue}
                    onKeyDown={handleKeyDown}
                    className="w-20 text-2xl font-bold tabular-nums text-center bg-slate-700 border border-primary-500 rounded-lg py-1 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    min="0"
                  />
                ) : (
                  <button
                    onClick={startEditing}
                    className="w-full text-center py-1 rounded-lg hover:bg-slate-700/50 transition-colors"
                  >
                    <span className="text-2xl font-bold tabular-nums">{goal.todayValue || 0}</span>
                  </button>
                )}
                <span className="text-xs text-slate-400 block">
                  / {goal.target_value} {goal.unit}
                </span>
              </div>
              <button
                onClick={() => handleNumericLog(1)}
                className="w-12 h-12 flex items-center justify-center bg-primary-600 hover:bg-primary-500 rounded-xl text-xl font-bold transition-colors shadow-lg shadow-primary-600/20 active:scale-90 transform"
              >
                +
              </button>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-400">This {goal.frequency === 'daily' ? 'week' : 'period'}</div>
              <div className="text-sm font-semibold">{goal.weekValue || 0} {goal.unit}</div>
            </div>
          </div>
          {/* Progress bar */}
          {goal.target_value && goal.target_value > 0 && (
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-500"
                style={{ width: `${percentComplete}%` }}
              />
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => navigate(`goal/${goal.id}/progress`)}
        className="w-full mt-3 py-2 text-xs font-medium text-slate-400 hover:text-white bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors"
      >
        View History
      </button>
    </div>
  );
}
